/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSearchParams, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Component imports
import { Avatar, Col, Divider, Drawer, Form, Input, Layout, List, Menu, Row, Space, Typography, Badge } from 'antd';
import { ClusterOutlined, LogoutOutlined, PlayCircleOutlined, SearchOutlined, UserOutlined } from '@ant-design/icons';
import BotAvatar from '../UI/BotAvatar';
import CreateBotModal from '../Modals/CreateBotModal';
import NotificationsListContainer from '../UI/NotificationsListContainer';
import BoxIcon from '../Icons/BoxIcon';
import { LiveChatSpaceIcon } from '../Icons/LiveChatSpaceIcon';
import { ObservatorySpaceIcon } from '../Icons/ObservatorySpaceIcon';
import { BotLabSpaceIcon } from '../Icons/BotLabSpaceIcon';
import { OrganizationSpaceIcon } from '../Icons/OrganizationSpaceIcon';

// Misc imports
import is from '../../utils/is';
import replaceUrlParams from '../../utils/string/replaceUrlParams';
import useUserPermissions from '../../utils/hooks/useUserPermissions';
import hasLiveChatAccess from '../../utils/misc/hasLiveChatAccess';
import { NAV_ANALYTICS, NAV_BOT, NAV_BOTS, NAV_CHAT_SESSIONS, NAV_CONTACTS, NAV_DASHBOARD, NAV_INTERACTION_LOGS, NAV_LIVECHAT, NAV_LOGOUT, NAV_MEDIA, NAV_MISSED_QUESTIONS, NAV_MONITORING, NAV_ORGANIZATION_INTEGRATIONS, NAV_ORGANIZATION_KNOWLEDGE_BASES, NAV_ORGANIZATION_LOGS, NAV_ORGANIZATION_SETTINGS, NAV_ORGANIZATION_TESTING, NAV_ORGANIZATION_USERS, NAV_SCHEDULED_REPORTS, NAV_SURVEYS } from '../../constants/navigation';
import { IS_AUTOMATED_AGENT_TESTING_ENABLED, IS_CONTACTS_ENABLED, IS_LIVECHAT_CONSOLE_ONLY, IS_LIVECHAT_CONVERSATIONS_ENABLED, IS_MEDIA_MANAGER_ENABLED, IS_NOTIFICATIONS_ENABLED, IS_SCHEDULED_REPORTS_ENABLED } from '../../constants/env';
import { ADDITIONAL_ROLES_ORG, PERMISSIONS_GROUP_ADMIN, PERMISSIONS_ORG } from '../../constants/main';
import { LIVECHAT_SCREEN_TABS } from '../../constants/liveChat';
import { setOrganization } from '../../store/auth/authActions';
import { getInitials } from '../../utils/string/getInitials';
import { updateRecentBots } from '../../store/bots/botsActions';
import { useFetchBotsQuery } from '../../store/bots/botsApi';
import { useFetchUnreadNotificationCountQuery } from '../../store/notifications/notificationsApi';

import OrgPlaceholderImage from '../../assets/placeholder_organization.svg';

// Constant declarations
const { Header } = Layout;
const { Text, Link, Title } = Typography;

const MENU_ITEMS = {
  // Parent items
  LIVECHAT: 'livechat-menu',
  DESIGNER: 'designer-menu',
  OBSERVATORY: 'observatory-menu',
  WORKSPACE: 'settings-menu',
  USER: 'user-menu',
  NOTIFICATIONS: 'notifications-menu',

  // Submenus
  USER_PROFILE: 'user-profile-option',
  LOGOUT: 'logout-option',
  ORGANIZATION_SUBMENU: 'organization-summenu',
  ORGANIZATION_SETTINGS: 'organization-settings',
  ORGANIZATION_INTEGRATIONS: 'organization-integrations',
  ORGANIZATION_KNOWLEDGE_BASES: 'organization-knowledge-bases',
  ORGANIZATION_MEDIA: 'organization-media',
  ORGANIZATION_LOGS: 'organization-logs',
  ORGANIZATION_USERS: 'organization-users',
  ORGANIZATION_CONTACTS: 'organization-contacts',
  ORGANIZATION_TESTING: 'organization-testing',

  // LiveChat submenus
  LIVECHAT_INBOX: 'livechat-inbox',
  LIVECHAT_HISTORY: 'livechat-history',
  LIVECHAT_SETTINGS: 'livechat-settings',

  // Observatory submenus
  OBSERVATORY_ANALYTICS: 'observatory-analytics',
  OBSERVATORY_CHAT_SESSIONS: 'observatory-chat-sessions',
  OBSERVATORY_MISSED_QUESTIONS: 'observatory-missed-questions',
  OBSERVATORY_SURVEYS: 'observatory-surveys',
  OBSERVATORY_MONITORING: 'observatory-monitoring',
  OBSERVATORY_INTERACTION_LOGS: 'observatory-interaction-logs',
  OBSERVATORY_SCHEDULED_REPORTS: 'observatory-scheduled-reports',
  OBSERVATORY_TESTS: 'observatory-tests',
  OBSERVATORY_RESULTS: 'observatory-results'
};

const baseClassName = 'mobile-navigation-bar';

const MobileNavBar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId } = useParams();
  const [botSearchFormMobile] = Form.useForm();
  const userPermissions = useUserPermissions(orgId);
  const { t } = useTranslation();

  const { currentUser } = useSelector((state) => state.auth);
  const { recentBots: allRecentBots } = useSelector((state) => state.bots);
  const { isFullScreenMode } = useSelector((state) => state.global);
  const { data: bots, isFetching: isLoadingBots, refetch: fetchBots } = useFetchBotsQuery({ orgId }, { skip: !orgId || !(is.array(userPermissions) && !is.emptyArray(userPermissions) && userPermissions.some(permission => Object.values(PERMISSIONS_ORG).includes(permission))) });

  const { data: unreadNotificationCountResponse } = useFetchUnreadNotificationCountQuery({ orgId, userId: currentUser.id }, { skip: IS_LIVECHAT_CONSOLE_ONLY || !IS_NOTIFICATIONS_ENABLED });
  const unreadNotificationCount = unreadNotificationCountResponse?.unreadNotificationCount || 0;

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [activeMenuItem, setActiveMenuItem] = useState(null);
  const [, updateState] = useState(); // Hook to force a component re-render
  const [isCreateBotModalOpen, setIsCreateBotModalOpen] = useState(false);

  const closeDeleteModal = () => {
    setIsCreateBotModalOpen(false);
  };

  const renderDesignerMenu = () => {
    const botSearch = botSearchFormMobile.getFieldValue('botSearch');
    const botsDatasource = botSearch ? (bots?.items ? [...bots.items] : [])
      .filter(bot => bot.displayName.toLowerCase().includes(botSearch.toLowerCase()))
      .sort((a, b) => (a.displayName > b.displayName) ? 1 : -1) : (bots?.items ? [...bots.items] : []);
    const recentBots = botSearch ? allRecentBots.filter(bot => bot.displayName.toLowerCase().includes(botSearch.toLowerCase())) : allRecentBots;

    return (
      <Form
        name="botSearchFormMobile"
        form={botSearchFormMobile}
        onFieldsChange={(_, allFields) => {
          updateState(allFields);
        }}
      >
        <Row>
          <Col span={24}>
            <Fragment>
              <div className="px-5">
                <Title level={5} className="fw-500 pt-3">{t('My Agents')}</Title>
              </div>
              <Divider className="mt-3 mb-0" />
            </Fragment>
          </Col>
        </Row>

        <Row className="px-5">
          <Col span={12} className="my-4">
            <Link onClick={() => navigate(replaceUrlParams(NAV_BOTS, { orgId }))} strong>
              {t('View All')}
            </Link>
          </Col>

          <Col span={12} className="my-4 text-right">
            <Link
              onClick={() => setIsCreateBotModalOpen(true)}
              strong
            >
              {t('Create New Agent')}
            </Link>
          </Col>
        </Row>

        <Row className="px-5 overflow-scroll">
          <Col span={24}>
            <Text strong>{t('Recent Agents')}</Text>

            <List
              grid={{ column: 2 }}
              dataSource={recentBots}
              className="mt-3"
              renderItem={bot => (
                <List.Item
                  className="mb-2 pr-14 pb-3 cursor-pointer"
                  onClick={() => {
                    if (location.pathname.includes(bot.id)) {
                      return;
                    } else {
                      navigate(replaceUrlParams(NAV_BOT, { tenantId: bot.id, orgId }));
                    }
                    setIsDrawerOpen(false);
                  }}
                >
                  <BotAvatar bot={bot} avatarSize={36} />
                </List.Item>
              )}
            />
          </Col>

          <Col span={24}>
            <Text strong>{t('All Agents')}</Text>

            <List
              grid={{ column: 2 }}
              dataSource={botsDatasource}
              loading={isLoadingBots && !bots}
              className="mt-3"
              style={{ maxHeight: 350 }}
              renderItem={bot => (
                <List.Item
                  className="mb-2 pr-14 pb-3 cursor-pointer"
                  onClick={() => {
                    if (location.pathname.includes(bot.id)) {
                      return;
                    } else {
                      navigate(replaceUrlParams(NAV_BOT, { tenantId: bot.id, orgId }));
                    }
                    setIsDrawerOpen(false);
                  }}
                >
                  <BotAvatar bot={bot} avatarSize={36} />
                </List.Item>
              )}
            />
          </Col>
        </Row>

        <Row className="p-5 bg-layout">
          <Col span={24}>
            <Form.Item
              name="botSearch"
              className="mb-0"
            >
              <Input
                size="large"
                placeholder={t('Search Agent')}
                prefix={<SearchOutlined className="text-secondary" />}
                allowClear
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    );
  };

  const renderObservatoryMenu = () => {
    const observatorySearchParams = {};

    return (
      <Menu
        mode="inline"
        triggerSubMenuAction="click"
        items={[
          {
            label: (
              <Fragment>
                <div className="px-5">
                  <Title level={5} className="fw-500 pt-3 pb-1">{t('Observatory')}</Title>
                </div>
                <Divider className="mt-3 mb-0" />
              </Fragment>
            ),
            key: 'observatory-menu-header',
            className: 'pointer-events-none h-auto px-0'
          },
          {
            icon: <BoxIcon name="list-ul" className="fs-xl" />,
            label: t('Sessions'),
            key: 'observatory-sessions',
            children: [
              {
                icon: <BoxIcon name="conversation" className="fs-xl" />,
                label: t('Chat Sessions'),
                key: MENU_ITEMS.OBSERVATORY_CHAT_SESSIONS
              },
              {
                icon: <BoxIcon name="question-mark" className="fs-xl" />,
                label: t('Missed Questions'),
                key: MENU_ITEMS.OBSERVATORY_MISSED_QUESTIONS
              },
              {
                icon: <BoxIcon name="clipboard" className="fs-xl" />,
                label: t('Surveys'),
                key: MENU_ITEMS.OBSERVATORY_SURVEYS
              }
            ]
          },
          {
            icon: <BoxIcon name="bar-chart-alt-2" className="fs-xl" />,
            label: t('Analytics'),
            key: MENU_ITEMS.OBSERVATORY_ANALYTICS,
            children: [
              {
                icon: <BoxIcon name="line-chart" className="fs-xl" />,
                label: t('Analytics'),
                key: 'observatory-analytics-analytics'
              },
              ...IS_SCHEDULED_REPORTS_ENABLED && userPermissions.includes(PERMISSIONS_ORG.ADMIN) ? [{
                icon: <BoxIcon name="file" className="fs-xl" />,
                label: t('Reports'),
                key: MENU_ITEMS.OBSERVATORY_SCHEDULED_REPORTS
              }] : []
            ]
          },
          {
            icon: <BoxIcon name="transfer-alt" className="fs-xl" />,
            label: t('Interaction Logs'),
            key: MENU_ITEMS.OBSERVATORY_INTERACTION_LOGS
          },
          {
            icon: <BoxIcon name="server" className="fs-xl" />,
            label: t('Monitoring'),
            key: MENU_ITEMS.OBSERVATORY_MONITORING
          },
          ...IS_AUTOMATED_AGENT_TESTING_ENABLED ? [{
            icon: <PlayCircleOutlined />,
            label: t('Testing'),
            key: 'observatory-agent-testing',
            children: [
              {
                icon: <BoxIcon name="badge-check" className="fs-xl" />,
                label: t('Tests'),
                key: MENU_ITEMS.OBSERVATORY_TESTS
              },
              {
                icon: <BoxIcon name="spreadsheet" className="fs-xl" />,
                label: t('Results'),
                key: MENU_ITEMS.OBSERVATORY_RESULTS
              }
            ]
          }] : []
        ]}
        onClick={(item) => {
          setIsDrawerOpen(false);

          switch (item.key) {
            case 'observatory-analytics-analytics':
              navigate(`${replaceUrlParams(NAV_ANALYTICS, { orgId })}?${createSearchParams({ ...observatorySearchParams }).toString()}`);
              break;
            case MENU_ITEMS.OBSERVATORY_SCHEDULED_REPORTS:
              navigate(`${replaceUrlParams(NAV_SCHEDULED_REPORTS, { orgId })}?${createSearchParams({ ...observatorySearchParams }).toString()}`);
              break;
            case MENU_ITEMS.OBSERVATORY_CHAT_SESSIONS:
              navigate(`${replaceUrlParams(NAV_CHAT_SESSIONS, { orgId })}?${createSearchParams({ ...observatorySearchParams }).toString()}`);
              break;
            case MENU_ITEMS.OBSERVATORY_MISSED_QUESTIONS:
              navigate(`${replaceUrlParams(NAV_MISSED_QUESTIONS, { orgId })}?${createSearchParams({ ...observatorySearchParams }).toString()}`);
              break;
            case MENU_ITEMS.OBSERVATORY_SURVEYS:
              navigate(`${replaceUrlParams(NAV_SURVEYS, { orgId })}?${createSearchParams({ ...observatorySearchParams }).toString()}`);
              break;
            case MENU_ITEMS.OBSERVATORY_INTERACTION_LOGS:
              navigate(`${replaceUrlParams(NAV_INTERACTION_LOGS, { orgId })}?${createSearchParams({ ...observatorySearchParams }).toString()}`);
              break;
            case MENU_ITEMS.OBSERVATORY_MONITORING:
              navigate(`${replaceUrlParams(NAV_MONITORING, { orgId })}?${createSearchParams({ ...observatorySearchParams }).toString()}`);
              break;
            case MENU_ITEMS.ORGANIZATION_TESTING:
              navigate(`${replaceUrlParams(NAV_ORGANIZATION_TESTING, { orgId })}?${createSearchParams({ ...observatorySearchParams }).toString()}`);
              break;
            default:
              break;
          }
        }}
      />
    );
  };

  const renderWorkspaceMenu = () => {
    return (
      <Menu
        mode="inline"
        triggerSubMenuAction="click"
        items={[
          {
            label: (
              <Fragment>
                <div className="px-5">
                  <Title level={5} className="fw-500 pt-3 pb-1">{t('My Workspace')}</Title>
                </div>
                <Divider className="mt-3 mb-0" />
              </Fragment>
            ),
            key: 'org-menu-header',
            className: 'pointer-events-none h-auto px-0'
          },
          ...userPermissions.includes(PERMISSIONS_ORG.ADMIN) ? [{
            icon: <BoxIcon name="group" className="fs-xl" />,
            label: t('Users'),
            key: MENU_ITEMS.ORGANIZATION_USERS,
            children: [
              {
                icon: <BoxIcon name="user" className="fs-xl" />,
                label: t('Users'),
                key: 'organization-users-users'
              },
              {
                icon: <BoxIcon name="group" className="fs-xl" />,
                label: t('User Groups'),
                key: 'organization-users-groups'
              },
              {
                icon: <BoxIcon name="envelope" className="fs-xl" />,
                label: t('User Invitations'),
                key: 'organization-users-invitations'
              }
            ]
          }] : [],
          {
            icon: <BoxIcon name="cog" className="fs-xl" />,
            label: t('Settings'),
            key: MENU_ITEMS.ORGANIZATION_SETTINGS,
            children: [
              {
                icon: <BoxIcon name="slider" className="fs-xl" />,
                label: t('Configuration'),
                key: 'organization-settings-configuration'
              },
              {
                icon: <BoxIcon name="lock" className="fs-xl" />,
                label: t('Login Settings'),
                key: 'organization-settings-login'
              }
            ]
          },
          {
            icon: <BoxIcon name="plug" className="fs-xl" />,
            label: t('Integrations'),
            key: MENU_ITEMS.ORGANIZATION_INTEGRATIONS
          },
          ...IS_MEDIA_MANAGER_ENABLED ? [{
            icon: <BoxIcon name="images" className="fs-xl" />,
            key: MENU_ITEMS.ORGANIZATION_MEDIA,
            label: t('Media Manager')
          }] : [],
          {
            icon: <BoxIcon name="book-alt" className="fs-xl" />,
            label: t('Knowledge Bases'),
            key: MENU_ITEMS.ORGANIZATION_KNOWLEDGE_BASES
          },
          ...userPermissions.includes(PERMISSIONS_ORG.ADMIN) ? [{
            icon: <BoxIcon name="list-ul" className="fs-xl" />,
            label: t('Audit Logs'),
            key: MENU_ITEMS.ORGANIZATION_LOGS
          }] : [],
          ...IS_CONTACTS_ENABLED ? [{
            icon: <BoxIcon name="desktop" className="fs-xl" />,
            label: t('Contacts'),
            key: MENU_ITEMS.ORGANIZATION_CONTACTS
          }] : []
        ]}
        onClick={(item) => {
          setIsDrawerOpen(false);

          switch (item.key) {
            case 'organization-settings-configuration':
              navigate(`${replaceUrlParams(NAV_ORGANIZATION_SETTINGS, { orgId })}?${createSearchParams({ tab: 'configuration' }).toString()}`);
              break;
            case 'organization-settings-login':
              navigate(`${replaceUrlParams(NAV_ORGANIZATION_SETTINGS, { orgId })}?${createSearchParams({ tab: 'login' }).toString()}`);
              break;
            case MENU_ITEMS.ORGANIZATION_SETTINGS:
              navigate(replaceUrlParams(NAV_ORGANIZATION_SETTINGS, { orgId }));
              break;
            case MENU_ITEMS.ORGANIZATION_INTEGRATIONS:
              navigate(replaceUrlParams(NAV_ORGANIZATION_INTEGRATIONS, { orgId }));
              break;
            case MENU_ITEMS.ORGANIZATION_KNOWLEDGE_BASES:
              navigate(replaceUrlParams(NAV_ORGANIZATION_KNOWLEDGE_BASES, { orgId }));
              break;
            case MENU_ITEMS.ORGANIZATION_MEDIA:
              navigate(replaceUrlParams(NAV_MEDIA, { orgId }));
              break;
            case MENU_ITEMS.ORGANIZATION_LOGS:
              navigate(replaceUrlParams(NAV_ORGANIZATION_LOGS, { orgId }));
              break;
            case 'organization-users-users':
              navigate(`${replaceUrlParams(NAV_ORGANIZATION_USERS, { orgId })}?${createSearchParams({ tab: 'users' }).toString()}`);
              break;
            case 'organization-users-groups':
              navigate(`${replaceUrlParams(NAV_ORGANIZATION_USERS, { orgId })}?${createSearchParams({ tab: 'user-groups' }).toString()}`);
              break;
            case 'organization-users-invitations':
              navigate(`${replaceUrlParams(NAV_ORGANIZATION_USERS, { orgId })}?${createSearchParams({ tab: 'user-invitations' }).toString()}`);
              break;
            case MENU_ITEMS.ORGANIZATION_USERS:
              navigate(replaceUrlParams(NAV_ORGANIZATION_USERS, { orgId }));
              break;
            case MENU_ITEMS.ORGANIZATION_CONTACTS:
              navigate(replaceUrlParams(NAV_CONTACTS, { orgId }));
              break;
            default:
              break;
          }
        }}
      />
    );
  };

  const renderLiveChatMenu = () => {
    const LIVECHAT_ADMIN_PERMISSIONS = [PERMISSIONS_ORG.ADMIN, ADDITIONAL_ROLES_ORG.LIVECHAT_ADMIN];

    return (
      <Menu
        mode="inline"
        triggerSubMenuAction="click"
        items={[
          {
            label: (
              <Fragment>
                <div className="px-5">
                  <Title level={5} className="fw-500 pt-3 pb-1">{t('LiveChat')}</Title>
                </div>
                <Divider className="mt-3 mb-0" />
              </Fragment>
            ),
            key: 'livechat-menu-header',
            className: 'pointer-events-none h-auto px-0'
          },
          {
            icon: <BoxIcon name="comment" className="fs-xl" />,
            label: t('Inbox'),
            key: MENU_ITEMS.LIVECHAT_INBOX
          },
          {
            icon: <BoxIcon name="history" className="fs-xl" />,
            label: t('History'),
            key: MENU_ITEMS.LIVECHAT_HISTORY
          },
          ...userPermissions.some(permission => LIVECHAT_ADMIN_PERMISSIONS.includes(permission)) ? [{
            icon: <BoxIcon name="cog" className="fs-xl" />,
            label: t('Settings'),
            key: MENU_ITEMS.LIVECHAT_SETTINGS
          }] : []
        ]}
        onClick={(item) => {
          setIsDrawerOpen(false);

          switch (item.key) {
            case MENU_ITEMS.LIVECHAT_INBOX:
              navigate({
                pathname: replaceUrlParams(NAV_LIVECHAT, { orgId }),
                search: createSearchParams({ tab: LIVECHAT_SCREEN_TABS.NEW }).toString()
              });
              break;
            case MENU_ITEMS.LIVECHAT_HISTORY:
              navigate({
                pathname: replaceUrlParams(NAV_LIVECHAT, { orgId }),
                search: createSearchParams({ tab: LIVECHAT_SCREEN_TABS.REQUESTS_HISTORY }).toString()
              });
              break;
            case MENU_ITEMS.LIVECHAT_SETTINGS:
              navigate({
                pathname: replaceUrlParams(NAV_LIVECHAT, { orgId }),
                search: createSearchParams({ tab: LIVECHAT_SCREEN_TABS.ADMIN_SETTINGS }).toString()
              });
              break;
            default:
              break;
          }
        }}
      />
    );
  };

  // Renders the submenu items for switching organizations
  const renderOrganizationsSubmenu = (menuId) => {
    const orgsDatasource = currentUser?.organizations?.length > 0
      ? currentUser.organizations
        .filter(org => org.id !== orgId)
        .sort((a, b) => a.displayName?.localeCompare(b.displayName))
      : [];

    return orgsDatasource.map(org => {
      return {
        icon: (
          <Avatar
            size={24}
            shape="square"
            src={org.image ?? OrgPlaceholderImage}
            className="bordered"
            style={{ top: -2 }} // tweak for alignment
          />
        ),
        label: org.displayName || <Text type="secondary">{t('(no name)')}</Text>,
        key: `${org.id}_${menuId}`
      };
    });
  };

  const renderUserMenu = () => {
    return (
      <Menu
        mode="inline"
        triggerSubMenuAction="click"
        items={[
          {
            label: (
              <Fragment>
                <div className="px-5 pt-3 pb-1">
                  <Title level={5} className="fw-500">{t('My Account')}</Title>
                  <Space size={10}>
                    <Avatar
                      size={34}
                      shape="square"
                      className="useravatar"
                    >
                      {getInitials(currentUser.fullName ?? '') || <UserOutlined className="v-align-middle fs-lg" />}
                    </Avatar>
                    <Text className="fs-lg">{currentUser.email}</Text>
                  </Space>
                </div>
                <Divider className="mt-3 mb-0" />
              </Fragment>
            ),
            key: 'user-menu-header',
            className: 'pointer-events-none h-auto px-0'
          },
          {
            icon: <UserOutlined />,
            label: t('My Profile'),
            key: MENU_ITEMS.USER_PROFILE
          },
          ...currentUser?.organizations?.length > 1 ? [{
            icon: <ClusterOutlined />,
            label: t('Switch Workspace'),
            key: MENU_ITEMS.ORGANIZATION_SUBMENU,
            children: renderOrganizationsSubmenu(MENU_ITEMS.ORGANIZATION_SUBMENU)
          }] : [],
          {
            icon: <LogoutOutlined />,
            label: t('Log Out'),
            key: MENU_ITEMS.LOGOUT
          }
        ]}
        onClick={(item) => {
          // Handler for all menu clicks, including submenus
          setIsDrawerOpen(false);

          switch (item.key) {
            case MENU_ITEMS.USER_PROFILE:
              // Add 'isProfileFormModalOpen' to the url params to open the My Profile form modal
              var searchParams = new URLSearchParams(location.search);
              searchParams.set('isProfileFormModalOpen', true);
              navigate({
                pathname: location.pathname,
                search: searchParams.toString()
              });
              return;
            case MENU_ITEMS.LOGOUT:
              navigate(NAV_LOGOUT);
              return;
            default:
              break;
          }

          // Else, if none of the switch() cases were met
          if (item.key.includes(MENU_ITEMS.ORGANIZATION_SUBMENU)) {
            const organizationId = item.key.split('_')?.[0];
            if (organizationId) {
              if (orgId !== organizationId) {
                dispatch(setOrganization(organizationId, navigate));
                dispatch(updateRecentBots([]));
                navigate(replaceUrlParams(NAV_DASHBOARD, { orgId: organizationId }));
              }
              setIsDrawerOpen(false);
            }
          }
        }}
      />
    );
  };

  const renderNotificationsMenu = () => {
    return <NotificationsListContainer isMobile={true} unreadNotificationCount={unreadNotificationCount}/>;
  };

  const renderDrawerContents = () => {
    switch (activeMenuItem) {
      case MENU_ITEMS.LIVECHAT:
        return renderLiveChatMenu();
      case MENU_ITEMS.DESIGNER:
        return renderDesignerMenu();
      case MENU_ITEMS.OBSERVATORY:
        return renderObservatoryMenu();
      case MENU_ITEMS.WORKSPACE:
        return renderWorkspaceMenu();
      case MENU_ITEMS.USER:
        return renderUserMenu();
      case MENU_ITEMS.NOTIFICATIONS:
        return renderNotificationsMenu();
      default:
        return null;
    }
  };

  const getMenuItems = () => {
    if (IS_LIVECHAT_CONSOLE_ONLY) {
      return [ ...userPermissions.some(permission => PERMISSIONS_GROUP_ADMIN.includes(permission)) ? [{
        key: MENU_ITEMS.WORKSPACE,
        label: (
          <div>
            <OrganizationSpaceIcon />
            {t('Settings')}
          </div>
        )
      }] : [],
      {
        key: MENU_ITEMS.USER,
        label: (
          <div>
            <UserOutlined />
            {t('Account')}
          </div>
        )
      }];
    }

    return [
      ...userPermissions.some(permission => PERMISSIONS_GROUP_ADMIN.includes(permission)) ? [{
        key: MENU_ITEMS.WORKSPACE,
        label: (
          <div>
            <OrganizationSpaceIcon />
            {t('Workspace')}
          </div>
        )
      }] : [],
      {
        key: MENU_ITEMS.DESIGNER,
        label: (
          <div>
            <BotLabSpaceIcon />
            {t('Designer')}
          </div>
        )
      },
      ...userPermissions.some(permission => Object.values(PERMISSIONS_ORG).includes(permission)) ? [{
        key: MENU_ITEMS.OBSERVATORY,
        label: (
          <div>
            <ObservatorySpaceIcon />
            {t('Observatory')}
          </div>
        )
      }] : [],
      ...(IS_LIVECHAT_CONSOLE_ONLY || IS_LIVECHAT_CONVERSATIONS_ENABLED) && hasLiveChatAccess(currentUser, orgId) ? [{
        key: MENU_ITEMS.LIVECHAT,
        label: (
          <div>
            <LiveChatSpaceIcon />
            {t('LiveChat')}
          </div>
        )
      }] : [],
      ...IS_NOTIFICATIONS_ENABLED
        ? [
          {
            key: MENU_ITEMS.NOTIFICATIONS,
            label: (
              <div className="d-flex flex-column">
                <Badge count={unreadNotificationCount}>
                  <BoxIcon name="bell" type="solid" />
                </Badge>
                {t('Notifications')}
              </div>
            )
          }
        ]
        : [],
      {
        key: MENU_ITEMS.USER,
        label: (
          <div>
            <BoxIcon name="user" type="solid" />
            {t('Account')}
          </div>
        )
      }
    ];
  };

  return (
    <Fragment>
      <Header className={isFullScreenMode ? `${baseClassName} d-none` : baseClassName}>
        <Menu
          mode="horizontal"
          className={`${baseClassName}__menu`}
          triggerSubMenuAction="click"
          items={getMenuItems()}
          onClick={(item) => {
            if (item.key === activeMenuItem) {
              setIsDrawerOpen(!isDrawerOpen);
              return;
            }

            switch (item.key) {
              case MENU_ITEMS.LIVECHAT:
                setActiveMenuItem(item.key);
                setIsDrawerOpen(true);
                return;
              case MENU_ITEMS.OBSERVATORY:
                setActiveMenuItem(item.key);
                setIsDrawerOpen(true);
                return;
              case MENU_ITEMS.DESIGNER:
                fetchBots();
                setActiveMenuItem(item.key);
                setIsDrawerOpen(true);
                return;
              case MENU_ITEMS.NOTIFICATIONS:
                setActiveMenuItem(item.key);
                setIsDrawerOpen(true);
                return;
              case MENU_ITEMS.WORKSPACE:
                setActiveMenuItem(item.key);
                setIsDrawerOpen(true);
                return;
              case MENU_ITEMS.USER:
                setActiveMenuItem(item.key);
                setIsDrawerOpen(true);
                return;
              default:
                break;
            }
          }}
        />
      </Header>

      <Drawer
        placement="bottom"
        closable={false}
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        zIndex={10}
        className={`${baseClassName}__drawer`}
      >
        {renderDrawerContents()}
      </Drawer>
      <CreateBotModal isModalOpen={isCreateBotModalOpen} closeModal={closeDeleteModal} />
    </Fragment>
  );
};

export default MobileNavBar;
